'use strict';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getWebSocketAuthMessage, WS_URL } from '../config/apiConfig';
import { updateDiscordPresence } from '../hooks/useDiscordPresence';
import { interruptForPvpMatch } from './interruptForPvpMatch';
import { showPvpMatchInterruptNotice } from './pvpMatchInterruptTiming';
import { shouldHandlePvpMatchFound, emitRematchClientMessage } from '../pvp/rematchWsBridge';
import {
  savePvpQueueIntent,
  readPvpQueueIntent,
  clearPvpQueueIntent,
  buildQueueStateFromIntent,
} from './pvpQueuePersistence';

const MatchmakingContext = createContext(null);

const EMPTY_QUEUE_STATE = {
  inQueue: false,
  strategyId: null,
  elapsedSeconds: 0,
  statusMessage: '',
};

export function useMatchmaking() {
  const ctx = useContext(MatchmakingContext);
  if (!ctx) {
    throw new Error('useMatchmaking must be used within MatchmakingProvider');
  }
  return ctx;
}

/**
 * Optional hook for pages outside provider tree during tests.
 */
export function useMatchmakingOptional() {
  return useContext(MatchmakingContext);
}

function readInitialQueueState() {
  try {
    const intent = readPvpQueueIntent();
    return buildQueueStateFromIntent(intent) || EMPTY_QUEUE_STATE;
  } catch (err) {
    console.error('Failed to read PvP queue intent:', err);
    clearPvpQueueIntent();
    return EMPTY_QUEUE_STATE;
  }
}

export function MatchmakingProvider({ children, isLoggedIn }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const location = useLocation();
  const locationPathRef = useRef(location.pathname);
  locationPathRef.current = location.pathname;
  const socketRef = useRef(null);
  const queueStartedAtRef = useRef(null);
  const authReadyRef = useRef(false);
  const pendingJoinRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;
  const [queueState, setQueueState] = useState(readInitialQueueState);
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    try {
      const intent = readPvpQueueIntent();
      if (intent) {
        queueStartedAtRef.current = intent.queuedAt;
      }
    } catch {
      queueStartedAtRef.current = null;
    }
  }, []);

  const applyQueueUi = useCallback((strategyId, queuedAt) => {
    const at = typeof queuedAt === 'number' && Number.isFinite(queuedAt) ? queuedAt : Date.now();
    queueStartedAtRef.current = at;
    savePvpQueueIntent(strategyId, at);
    setQueueState({
      inQueue: true,
      strategyId: String(strategyId),
      elapsedSeconds: Math.max(0, Math.floor((Date.now() - at) / 1000)),
      statusMessage: '',
    });
    updateDiscordPresence('In Queue', null, true);
  }, []);

  const clearQueueUi = useCallback(() => {
    queueStartedAtRef.current = null;
    pendingJoinRef.current = null;
    clearPvpQueueIntent();
    setQueueState(EMPTY_QUEUE_STATE);
    updateDiscordPresence('In Menu');
  }, []);

  const closeSocket = useCallback(
    (sendLeaveQueue) => {
      intentionalCloseRef.current = true;
      const socket = socketRef.current;
      if (socket && sendLeaveQueue && socket.readyState === WebSocket.OPEN) {
        try {
          socket.send(JSON.stringify({ type: 'leave-queue' }));
        } catch {
          /* ignore */
        }
      }
      if (socket) {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      }
      socketRef.current = null;
      authReadyRef.current = false;
      if (sendLeaveQueue) {
        clearQueueUi();
      }
    },
    [clearQueueUi]
  );

  const sendJoinQueue = useCallback(
    (socket, strategyId, queuedAt) => {
      applyQueueUi(strategyId, queuedAt);
      socket.send(
        JSON.stringify({
          type: 'join-queue',
          strategyId,
        })
      );
    },
    [applyQueueUi]
  );

  const restoreQueueAfterAuth = useCallback(
    (socket) => {
      if (pendingJoinRef.current) {
        const { strategyId, queuedAt } = pendingJoinRef.current;
        pendingJoinRef.current = null;
        sendJoinQueue(socket, strategyId, queuedAt);
        return;
      }
      let intent;
      try {
        intent = readPvpQueueIntent();
      } catch (err) {
        console.error('Invalid PvP queue intent on restore:', err);
        clearPvpQueueIntent();
        return;
      }
      if (!intent) {
        return;
      }
      sendJoinQueue(socket, intent.strategyId, intent.queuedAt);
    },
    [sendJoinQueue]
  );

  const syncQueueFromServer = useCallback(
    (queueInfo) => {
      if (!queueInfo || queueInfo.inQueue !== true) {
        return;
      }
      const strategyId = queueInfo.strategyId != null ? String(queueInfo.strategyId) : null;
      if (!strategyId) {
        throw new Error('syncQueueFromServer: strategyId is required when inQueue is true');
      }
      const queuedAt =
        typeof queueInfo.queuedAt === 'number' && Number.isFinite(queueInfo.queuedAt)
          ? queueInfo.queuedAt
          : Date.now();
      applyQueueUi(strategyId, queuedAt);
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN && authReadyRef.current) {
        socket.send(JSON.stringify({ type: 'join-queue', strategyId }));
      } else {
        pendingJoinRef.current = { strategyId, queuedAt };
      }
    },
    [applyQueueUi]
  );

  const connectLobbySocket = useCallback(() => {
    const existing = socketRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)
    ) {
      return existing;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    intentionalCloseRef.current = false;
    const socket = new window.WebSocket(WS_URL);
    socketRef.current = socket;

    socket.onopen = () => {
      intentionalCloseRef.current = false;
      authReadyRef.current = false;
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage, { connectionRole: 'lobby' })));
    };

    socket.onmessage = async (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'auth-success') {
        authReadyRef.current = true;
        restoreQueueAfterAuth(socket);
      }

      if (data.type === 'queue-joined' || data.type === 'queue-waiting') {
        setQueueState((prev) => {
          if (!prev.inQueue || !prev.strategyId) {
            return prev;
          }
          savePvpQueueIntent(prev.strategyId, queueStartedAtRef.current || Date.now());
          return {
            ...prev,
            inQueue: true,
            statusMessage: '',
          };
        });
      }

      if (data.type === 'queue-left') {
        clearQueueUi();
      }

      if (
        data.type === 'rematch-waiting' ||
        data.type === 'rematch-offered' ||
        data.type === 'rematch-cancelled'
      ) {
        emitRematchClientMessage(data);
      }

      if (data.type === 'match-found') {
        if (data.gameState?.isRematch === true || data.gameState?.isMirrorRematch === true) {
          emitRematchClientMessage(data);
        }
        if (!shouldHandlePvpMatchFound(data.gameId)) {
          return;
        }
        clearQueueUi();
        const matchPayload = {
          gameId: data.gameId,
          gameState: data.gameState,
          yourTurn: data.yourTurn,
          strategyId: localStorage.getItem('selectedPvpStrategy') || undefined,
        };
        try {
          try {
            await showPvpMatchInterruptNotice(tRef.current('matchmaking.pvpMatchFoundSwitching'));
          } catch (noticeErr) {
            console.error('PvP interrupt notice failed:', noticeErr);
          }
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('coda-pvp-interrupt', { detail: { gameId: matchPayload.gameId } })
            );
          }
          await interruptForPvpMatch(navigateRef.current, locationPathRef.current, matchPayload);
        } catch (err) {
          console.error('PvP interrupt failed:', err);
        }
      }

      if (data.type === 'pvp-match-ended') {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('coda-pvp-match-ended', { detail: { gameId: data.gameId } }));
        }
      }

      if (data.type === 'error') {
        console.error('Matchmaking error:', data.message);
        setQueueState((prev) => ({
          ...prev,
          inQueue: false,
          statusMessage: data.message || '',
        }));
        updateDiscordPresence('In Menu');
      }
    };

    socket.onclose = () => {
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      authReadyRef.current = false;
      if (intentionalCloseRef.current) {
        return;
      }
      if (isLoggedInRef.current && localStorage.getItem('token')) {
        setTimeout(() => {
          if (!socketRef.current && isLoggedInRef.current) {
            connectLobbySocket();
          }
        }, 500);
      }
    };

    return socket;
  }, [restoreQueueAfterAuth, clearQueueUi]);

  useEffect(() => {
    if (!isLoggedIn) {
      closeSocket(true);
      return undefined;
    }
    connectLobbySocket();
    return undefined;
  }, [isLoggedIn, closeSocket, connectLobbySocket]);

  useEffect(() => {
    if (!queueState.inQueue) {
      return undefined;
    }
    const interval = setInterval(() => {
      if (queueStartedAtRef.current == null) {
        return;
      }
      const elapsed = Math.floor((Date.now() - queueStartedAtRef.current) / 1000);
      setQueueState((prev) => ({ ...prev, elapsedSeconds: elapsed }));
    }, 1000);
    return () => clearInterval(interval);
  }, [queueState.inQueue]);

  useEffect(() => {
    if (!queueState.inQueue || !queueState.strategyId) {
      return undefined;
    }
    const interval = setInterval(() => {
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN && authReadyRef.current) {
        socket.send(JSON.stringify({ type: 'join-queue', strategyId: queueState.strategyId }));
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [queueState.inQueue, queueState.strategyId]);

  const joinQueue = useCallback(
    (strategyId) => {
      if (!strategyId) {
        throw new Error('joinQueue: strategyId is required');
      }
      let socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        pendingJoinRef.current = { strategyId, queuedAt: Date.now() };
        applyQueueUi(strategyId, Date.now());
        connectLobbySocket();
        return;
      }
      if (!authReadyRef.current) {
        pendingJoinRef.current = { strategyId, queuedAt: Date.now() };
        applyQueueUi(strategyId, Date.now());
        return;
      }
      sendJoinQueue(socket, strategyId, Date.now());
    },
    [applyQueueUi, connectLobbySocket, sendJoinQueue]
  );

  const leaveQueue = useCallback(() => {
    closeSocket(true);
  }, [closeSocket]);

  const value = useMemo(
    () => ({
      queueState,
      joinQueue,
      leaveQueue,
      syncQueueFromServer,
      isInQueue: queueState.inQueue,
    }),
    [queueState, joinQueue, leaveQueue, syncQueueFromServer]
  );

  return <MatchmakingContext.Provider value={value}>{children}</MatchmakingContext.Provider>;
}
